import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { LoadingIndicator } from "@/components/LoadingIndicator";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useCreateAddCommissionLedgerEntry } from "@/hooks/createAdds/useCreateAddCommissionLedgerEntry";
import { formatting } from "@/lib/formatting";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { CommissionLedgerEntryType, EarningsPlacement } from "@/types";

export const AddCommissionAdjustmentDialog = ({
  ledgerNetDollars,
  onOpenChange,
  open,
  placement,
  stafferName,
}: {
  // Current ledger total for the placement — the floor for negative adjustments
  ledgerNetDollars: number;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  placement: EarningsPlacement;
  stafferName: string;
}) => {
  /* Database CRUD Operations */
  // Create
  const {
    mutate: createLedgerEntry,
    isPending,
    isError,
  } = useCreateAddCommissionLedgerEntry();

  /* React Hooks */
  const adjustmentSchema = z.object({
    amount: z.coerce
      .number({ invalid_type_error: "Enter a dollar amount" })
      .refine((amount) => amount !== 0, {
        message: "Enter a non-zero dollar amount",
      })
      .refine((amount) => amount >= -ledgerNetDollars, {
        message: `Can't remove more than the ${formatting.currency(
          ledgerNetDollars,
        )} currently on this placement's ledger`,
      }),
    reason: z.string().min(1, { message: "An adjustment reason is required" }),
  });

  const form = useForm<z.infer<typeof adjustmentSchema>>({
    resolver: zodResolver(adjustmentSchema),
    // Empty string so the input starts blank with just the placeholder
    defaultValues: { amount: "" as unknown as number, reason: "" },
  });

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [open, form]);

  const enteredAmount = form.watch("amount");

  /* Handlers */
  const handleSubmit = (data: z.infer<typeof adjustmentSchema>) => {
    mixpanelUtils.trackButtonClick(
      "Earnings add commission adjustment",
      MIXPANEL_SCREENS.EARNINGS,
    );
    createLedgerEntry(
      {
        placementId: placement.id,
        type: CommissionLedgerEntryType.ADJUSTMENT,
        amount: data.amount,
        reason: data.reason,
      },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  /* Render */
  const render = () => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add commission adjustment</DialogTitle>
          <DialogDescription>
            You are adding an adjustment to the commission amount earned by{" "}
            {stafferName} for placing {placement.candidateName}. Double check
            this is your intent and the adjustment amount is correct before
            submitting.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adjustment amount</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoFocus
                      type="number"
                      step="any"
                      placeholder="ex: -1000"
                      // People type amounts; spinner arrows are just noise
                      className="[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    />
                  </FormControl>
                  <FormMessage />
                  {Number(enteredAmount) > 0 ? (
                    <Text className="text-xs font-medium text-red-600">
                      Careful, you are currently adding to the commission
                      amount for this placement.
                    </Text>
                  ) : null}
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Adjustment reason</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Why is this adjustment needed?"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {isError ? (
              <Text className="text-xs font-medium text-red-600">
                Failed to add adjustment. Try again.
              </Text>
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                className="rounded-full"
                disabled={isPending}
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="w-36 rounded-full"
                disabled={isPending}
              >
                {isPending ? (
                  <LoadingIndicator gearColor="white" variant="small" />
                ) : (
                  "Add adjustment"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );

  return render();
};
